// src/pages/CAControl.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Tabs,
  Card,
  Space,
  Button,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Dropdown,
  Checkbox,
  Popconfirm,
  Typography,
  Tooltip,
  Divider,
} from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  CopyOutlined,
  KeyOutlined,
  FileTextOutlined,
  SettingOutlined,
  FormOutlined,
  MoreOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useDataProvider, useNotification } from "@refinedev/core";
import type { ColumnsType, TableProps } from "antd/es/table";
import type { MenuProps } from "antd";

import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import { downloadFile } from "../utils/downloadFile";
import { usePGBroadcastRefresh } from "../realtime/usePGBroadcastRefresh";

const CA_SCHEMA = "ca";
const { Text } = Typography;

// --- Types ---

type UUID = string;

type CertAuthority = {
  id: UUID;
  ca_name: string;
  common_name: string;
  certificate_pem: string;
  is_root: boolean;
  not_before: string;
  not_after: string;
  status_id: number;
  parent_ca_id: UUID | null;
  default_policy_id: UUID | null;
};

type Certificate = {
  id: UUID;
  issuing_ca_id: UUID;
  common_name: string;
  certificate_pem: string;
  subject_alternative_names?: string[] | null;
  not_before: string;
  not_after: string;
  revoked_at: string | null;
  status_id: number;
};

type IssuancePolicy = {
  id: UUID;
  ca_id: UUID;
  policy_name: string;
  description?: string | null;
  is_active: boolean;
  priority: number | null;
  min_validity_days: number | null;
  max_validity_days: number | null;
  require_san?: boolean | null;
  cn_regex?: string | null;
  allowed_san_dns_suffix?: string[] | null;
};

type Template = {
  id: UUID;
  template_name: string;
  cert_type_id: number;
  key_algorithm_id: number;
  key_size: number;
  hash_algorithm_id: number;
  validity_period_days: number;
  key_usage: string[];
  extended_key_usage: string[] | null;
  subject_template: any | null;
  san_template: any | null;
  is_active: boolean;
};

type GroupRow = { id: string; name: string };

// Generic Share Row for all CA types
type ShareRow = {
  id: string;
  group_id: string;
  can_read: boolean;
  can_create: boolean;
  can_update: boolean;
  can_delete: boolean;
  can_admin: boolean;
  created_at?: string;

  // Potential FKs
  certificate_authority_id?: string;
  certificate_id?: string;
  issuance_policy_id?: string;
  certificate_template_id?: string;
};

type ExportFormat =
  | "pem"
  | "chain_pem"
  | "pem_key"
  | "chain_pem_key"
  | "der"
  | "p7b"
  | "pfx"
  | "p12"
  | "root_pem";

// --- Helpers ---

const formatNeedsPassword = (format: ExportFormat): boolean =>
  format === "pfx" ||
  format === "p12" ||
  format === "pem_key" ||
  format === "chain_pem_key";

const mimeFromFormat = (format: ExportFormat): string => {
  switch (format) {
    case "pem":
    case "chain_pem":
    case "pem_key":
    case "chain_pem_key":
    case "root_pem":
      return "application/x-pem-file";
    case "der":
      return "application/x-x509-ca-cert";
    case "p7b":
      return "application/x-pkcs7-certificates";
    case "pfx":
    case "p12":
      return "application/x-pkcs12";
    default:
      return "application/octet-stream";
  }
};

const extFromFormat = (format: ExportFormat): string => {
  switch (format) {
    case "pem":
    case "pem_key":
    case "chain_pem":
    case "chain_pem_key":
    case "root_pem":
      return "pem";
    case "der":
      return "der";
    case "p7b":
      return "p7b";
    case "pfx":
      return "pfx";
    case "p12":
      return "p12";
    default:
      return "bin";
  }
};

const byteStringToBlob = (data: any, mime: string): Blob => {
  if (data instanceof Blob) return data;
  if (
    data &&
    typeof data === "object" &&
    "data" in data &&
    typeof (data as any).data === "string"
  ) {
    return byteStringToBlob((data as any).data, mime);
  }
  if (typeof data !== "string") {
    const json = JSON.stringify(data, null, 2);
    return new Blob([json], { type: "application/json" });
  }
  let bytes: Uint8Array;
  if (data.startsWith("\\x") || data.startsWith("0x")) {
    const hex = data.replace(/^\\x|^0x/, "");
    const len = hex.length / 2;
    bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
  } else {
    const bin = atob(data);
    const len = bin.length;
    bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = bin.charCodeAt(i);
    }
  }
  return new Blob([bytes], { type: mime });
};

const filenameFromCertAndFormat = (
  cert: Certificate,
  format: ExportFormat,
): string => {
  const baseRaw = cert.common_name || `cert-${cert.id}`;
  const base = baseRaw.replace(/[^\w.-]+/g, "_");
  const ext = extFromFormat(format);
  return `${base}.${ext}`;
};

// --- Resizable Table Components ---
type ResizableTitleProps = React.HTMLAttributes<any> & {
  width?: number;
  onResize?: (e: any, data: { size: { width: number } }) => void;
};
const ResizableTitle: React.FC<ResizableTitleProps> = (props) => {
  const { onResize, width, style, ...rest } = props;
  if (!width) return <th {...rest} />;
  return (
    <Resizable
      width={width}
      height={0}
      onResize={onResize!}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...rest} style={{ ...style, whiteSpace: "nowrap" }} />
    </Resizable>
  );
};

function useResizableColumns<T extends { key?: React.Key }>(
  base: ColumnsType<T>,
  initialWidths: number[],
): { columns: ColumnsType<T>; components: TableProps<T>["components"] } {
  const [cols, setCols] = React.useState(
    base.map((c, i) => ({
      ...c,
      width: initialWidths[i] ?? (c.width as number) ?? 120,
    })),
  );
  const handleResize =
    (index: number) =>
    (_: any, { size }: { size: { width: number } }) => {
      setCols((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], width: Math.max(size.width, 80) };
        return next;
      });
    };
  const columns = cols.map((col, index) => ({
    ...col,
    onHeaderCell: (column: any) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));
  const components: TableProps<T>["components"] = {
    header: { cell: ResizableTitle },
  };
  return { columns, components };
}

// ==========================================
//  SHARES BROWSER COMPONENT
// ==========================================

function isWritableShare(s: ShareRow) {
  return !!(s.can_create || s.can_update || s.can_delete || s.can_admin);
}

const SharesBrowser: React.FC<{
  title: string;
  shareResource: string;
  fkField: string;
  parentResource: string;
  parentLabel: (row: any) => string;
  refreshToken?: number;
}> = ({
  title,
  shareResource,
  fkField,
  parentResource,
  parentLabel,
  refreshToken,
}) => {
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open } = useNotification();
  const [modal, contextHolder] = Modal.useModal();

  const notifyError = (err: any, fallback = "Action failed") => {
    const msg = err?.message || fallback;
    const details = err?.body?.details || err?.body?.hint;
    if (open) open({ type: "error", message: msg, description: details });
    else message.error(msg);
  };

  const notifySuccess = (msg: string) => {
    if (open) open({ type: "success", message: msg });
    else message.success(msg);
  };

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [parents, setParents] = useState<{ id: string; label: string }[]>([]);
  const [shares, setShares] = useState<ShareRow[]>([]);

  // Filters
  const [search, setSearch] = useState("");
  const [filterGroupId, setFilterGroupId] = useState<string | undefined>(
    undefined,
  );
  const [filterParentId, setFilterParentId] = useState<string | undefined>(
    undefined,
  );
  const [onlyAdmin, setOnlyAdmin] = useState(false);
  const [onlyWritable, setOnlyWritable] = useState(false);

  const [addForm] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const groupNameById = useMemo(
    () => new Map(groups.map((g) => [g.id, g.name])),
    [groups],
  );
  const parentLabelById = useMemo(
    () => new Map(parents.map((p) => [p.id, p.label])),
    [parents],
  );

  // Which groups are already shared with the currently selected parent?
  const existingGroupIdsForSelectedParent = useMemo(() => {
    if (!filterParentId) return new Set<string>();
    const s = new Set<string>();
    for (const row of shares) {
      const pid = (row as any)[fkField] as string | undefined;
      if (pid === filterParentId) s.add(row.group_id);
    }
    return s;
  }, [shares, fkField, filterParentId]);

  const clearFilters = () => {
    setSearch("");
    setFilterGroupId(undefined);
    setFilterParentId(undefined);
    setOnlyAdmin(false);
    setOnlyWritable(false);
    setSelectedRowKeys([]);
  };

  const loadLookups = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    const [gRes, pRes] = await Promise.all([
      dataProvider.getList({
        resource: "groups",
        pagination: { current: 1, pageSize: 5000 },
        sorters: [{ field: "name", order: "asc" }],
        meta: { schema: "iam" },
      }),
      dataProvider.getList({
        resource: parentResource,
        pagination: { current: 1, pageSize: 5000 },
        sorters: [{ field: "created_at", order: "desc" }],
        meta: { schema: CA_SCHEMA },
      }),
    ]);
    setGroups((gRes.data || []) as GroupRow[]);
    const parentRows = (pRes.data || []).map((r: any) => ({
      id: r.id,
      label: parentLabel(r),
    }));
    setParents(parentRows);
  }, [dataProvider, parentResource, parentLabel]);

  const loadShares = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    const filters: any[] = [];
    if (filterGroupId)
      filters.push({ field: "group_id", operator: "eq", value: filterGroupId });
    if (filterParentId)
      filters.push({ field: fkField, operator: "eq", value: filterParentId });

    const res = await dataProvider.getList({
      resource: shareResource,
      pagination: { current: 1, pageSize: 5000 },
      sorters: [{ field: "created_at", order: "desc" }],
      filters,
      meta: { schema: CA_SCHEMA },
    });
    setShares((res.data || []) as ShareRow[]);
  }, [dataProvider, shareResource, fkField, filterGroupId, filterParentId]);

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadLookups(), loadShares()]);
    } catch (e: any) {
      notifyError(e, "Failed to load shares");
    } finally {
      setLoading(false);
    }
  }, [loadLookups, loadShares]);

  // ✅ keep these 3 effects (this is fine)
  useEffect(() => {
    // When parent changes, clear the group in the Add form so we don't accidentally try to add a hidden duplicate
    addForm.setFieldsValue({ group_id: undefined });
  }, [filterParentId, addForm]);

  useEffect(() => {
    loadAll().catch(() => {});
  }, [loadAll, refreshToken]);

  useEffect(() => {
    // optional: reload shares only when server-side filters change
    loadShares().catch(() => {});
  }, [filterGroupId, filterParentId, loadShares]);

  const filteredShares = useMemo(() => {
    let rows = shares;
    if (onlyAdmin) rows = rows.filter((s) => !!s.can_admin);
    if (onlyWritable) rows = rows.filter((s) => isWritableShare(s));

    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((s) => {
      const g = groupNameById.get(s.group_id) || s.group_id;
      const pid = (s as any)[fkField] as string | undefined;
      const p = pid ? parentLabelById.get(pid) || pid : "";
      const blob = `${g} ${p}`.toLowerCase();
      return blob.includes(q);
    });
  }, [
    shares,
    onlyAdmin,
    onlyWritable,
    search,
    groupNameById,
    parentLabelById,
    fkField,
  ]);

  const addShare = async () => {
    try {
      const v = await addForm.validateFields();
      setLoading(true);
      const dup = shares.some((s) => {
        const pid = (s as any)[fkField];
        return pid === v.parent_id && s.group_id === v.group_id;
      });
      if (dup) {
        message.warning("That group is already shared to this item.");
        return;
      }

      await dataProvider.create({
        resource: shareResource,
        variables: {
          [fkField]: v.parent_id,
          group_id: v.group_id,
          can_read: true,
          can_create: !!v.can_create,
          can_update: !!v.can_update,
          can_delete: !!v.can_delete,
          can_admin: !!v.can_admin,
        },
        meta: { schema: CA_SCHEMA },
      });

      notifySuccess("Share added");
      addForm.resetFields([
        "group_id",
        "can_create",
        "can_update",
        "can_delete",
        "can_admin",
      ]);
      await loadShares();
    } catch (e: any) {
      notifyError(e, "Failed to add share");
    } finally {
      setLoading(false);
    }
  };

  const updateShare = async (row: ShareRow, patch: Partial<ShareRow>) => {
    try {
      setLoading(true);
      await dataProvider.update({
        resource: shareResource,
        id: row.id,
        variables: patch,
        meta: { schema: CA_SCHEMA },
      });
      // Optimistic update
      setShares((prev) =>
        prev.map((s) => (s.id === row.id ? { ...s, ...patch } : s)),
      );
    } catch (e: any) {
      notifyError(e, "Failed to update share");
      await loadShares();
    } finally {
      setLoading(false);
    }
  };

  const removeShare = async (row: ShareRow) => {
    try {
      setLoading(true);
      await dataProvider.deleteOne({
        resource: shareResource,
        id: row.id,
        meta: { schema: CA_SCHEMA },
      });
      notifySuccess("Share removed");
      setShares((prev) => prev.filter((s) => s.id !== row.id));
    } catch (e: any) {
      notifyError(e, "Failed to remove share");
    } finally {
      setLoading(false);
    }
  };

  const bulkRemove = async () => {
    const ids = selectedRowKeys.map(String);
    if (!ids.length) return;
    modal.confirm({
      title: `Remove ${ids.length} share(s)?`,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true);
        try {
          for (const id of ids) {
            await dataProvider.deleteOne({
              resource: shareResource,
              id,
              meta: { schema: CA_SCHEMA },
            });
          }
          setSelectedRowKeys([]);
          notifySuccess("Removed selected shares");
          await loadShares();
        } catch (e: any) {
          notifyError(e, "Bulk remove failed");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Filtered group options for the Add dropdown
  const groupOptions = useMemo(() => {
    if (!filterParentId)
      return groups.map((g) => ({ value: g.id, label: g.name }));
    return groups
      .filter((g) => !existingGroupIdsForSelectedParent.has(g.id))
      .map((g) => ({ value: g.id, label: g.name }));
  }, [groups, filterParentId, existingGroupIdsForSelectedParent]);

  const parentOptions = useMemo(
    () => parents.map((p) => ({ value: p.id, label: p.label })),
    [parents],
  );

  return (
    <div>
      {contextHolder}
      <Card
        size="small"
        title={title}
        bodyStyle={{ padding: 12 }}
        style={{ marginBottom: 12 }}
      >
        <Space wrap>
          <Input
            allowClear
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240 }}
            suffix={
              search ? (
                <CloseCircleOutlined
                  onClick={() => setSearch("")}
                  style={{ cursor: "pointer" }}
                />
              ) : null
            }
          />
          <Select
            allowClear
            size="small"
            style={{ width: 240 }}
            placeholder="Filter by group"
            value={filterGroupId}
            onChange={(v) => setFilterGroupId(v)}
            options={groupOptions}
            showSearch
            optionFilterProp="label"
          />
          <Select
            allowClear
            size="small"
            style={{ width: 360 }}
            placeholder="Filter by Item"
            value={filterParentId}
            onChange={(v) => setFilterParentId(v)}
            options={parentOptions}
            showSearch
            optionFilterProp="label"
          />
          <Checkbox
            checked={onlyAdmin}
            onChange={(e) => setOnlyAdmin(e.target.checked)}
          >
            Only admin
          </Checkbox>
          <Checkbox
            checked={onlyWritable}
            onChange={(e) => setOnlyWritable(e.target.checked)}
          >
            Only writable
          </Checkbox>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={loadAll}
            loading={loading}
          >
            Refresh
          </Button>
          <Button size="small" onClick={clearFilters} disabled={loading}>
            Clear filters
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={!selectedRowKeys.length || loading}
            onClick={bulkRemove}
          >
            Remove selected ({selectedRowKeys.length})
          </Button>
        </Space>
      </Card>

      <Card
        size="small"
        title="Add Share"
        bodyStyle={{ padding: 12 }}
        style={{ marginBottom: 12 }}
      >
        <Form
          form={addForm}
          layout="inline"
          style={{ gap: 8, flexWrap: "wrap" }}
          initialValues={{
            can_create: false,
            can_update: false,
            can_delete: false,
            can_admin: false,
          }}
        >
          <Form.Item
            name="parent_id"
            rules={[{ required: true, message: "Item required" }]}
          >
            <Select
              size="small"
              style={{ width: 420 }}
              placeholder="Select Item to Share"
              options={parentOptions}
              showSearch
              optionFilterProp="label"
              onChange={(v) => setFilterParentId(v)}
            />
          </Form.Item>
          <Form.Item
            name="group_id"
            rules={[{ required: true, message: "Group required" }]}
          >
            <Select
              size="small"
              style={{ width: 240 }}
              placeholder={filterParentId ? "Select group" : "Select item first"}
              options={groupOptions}
              showSearch
              optionFilterProp="label"
              disabled={!filterParentId}
            />
          </Form.Item>
          <Form.Item name="can_create" valuePropName="checked">
            <Checkbox>Create</Checkbox>
          </Form.Item>
          <Form.Item name="can_update" valuePropName="checked">
            <Checkbox>Update</Checkbox>
          </Form.Item>
          <Form.Item name="can_delete" valuePropName="checked">
            <Checkbox>Delete</Checkbox>
          </Form.Item>
          <Form.Item name="can_admin" valuePropName="checked">
            <Checkbox>Admin</Checkbox>
          </Form.Item>
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={addShare}
            loading={loading}
          >
            Add
          </Button>
        </Form>
        <Divider style={{ margin: "10px 0" }} />
        <Text type="secondary">
          These shares grant access to a <b>group</b> via the{" "}
          <code>iam.groups</code> system.
        </Text>
      </Card>

      <Table<ShareRow>
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={filteredShares}
        pagination={{ pageSize: 25 }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        columns={[
          {
            title: "Group",
            dataIndex: "group_id",
            width: 260,
            render: (gid: string) => groupNameById.get(gid) ?? gid,
          },
          {
            title: "Item",
            width: 520,
            render: (_, r) => {
              const pid = (r as any)[fkField] as string | undefined;
              if (!pid) return <Text type="secondary">—</Text>;
              return parentLabelById.get(pid) ?? pid;
            },
          },
          {
            title: "Create",
            dataIndex: "can_create",
            width: 90,
            render: (v: boolean, r) => (
              <Checkbox
                checked={!!v}
                onChange={(e) =>
                  updateShare(r, { can_create: e.target.checked })
                }
              />
            ),
          },
          {
            title: "Update",
            dataIndex: "can_update",
            width: 90,
            render: (v: boolean, r) => (
              <Checkbox
                checked={!!v}
                onChange={(e) =>
                  updateShare(r, { can_update: e.target.checked })
                }
              />
            ),
          },
          {
            title: "Delete",
            dataIndex: "can_delete",
            width: 90,
            render: (v: boolean, r) => (
              <Checkbox
                checked={!!v}
                onChange={(e) =>
                  updateShare(r, { can_delete: e.target.checked })
                }
              />
            ),
          },
          {
            title: "Admin",
            dataIndex: "can_admin",
            width: 90,
            render: (v: boolean, r) => (
              <Checkbox
                checked={!!v}
                onChange={(e) =>
                  updateShare(r, { can_admin: e.target.checked })
                }
              />
            ),
          },
          {
            title: "",
            width: 70,
            render: (_, r) => (
              <Popconfirm
                title="Remove share?"
                onConfirm={() => removeShare(r)}
                okText="Remove"
                cancelText="Cancel"
              >
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

// ==========================================
//  MAIN COMPONENT
// ==========================================

const CAControl: React.FC = () => {
  const getDp = useDataProvider();
  const dp = getDp();
  const { open } = useNotification();

  const [activeTab, setActiveTab] = useState<
    | "authorities"
    | "certificates"
    | "policies"
    | "templates"
    | "ca_shares"
    | "cert_shares"
    | "policy_shares"
    | "template_shares"
  >("authorities");

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [masterPassword, setMasterPassword] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const [authorities, setAuthorities] = useState<CertAuthority[]>([]);
  const [certs, setCerts] = useState<Certificate[]>([]);
  const [policies, setPolicies] = useState<IssuancePolicy[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [groups, setGroups] = useState<any[]>([]);

  // certificate download modal state
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadPassword, setDownloadPassword] = useState("");
  const [downloadSubmitting, setDownloadSubmitting] = useState(false);
  const [downloadRow, setDownloadRow] = useState<Certificate | null>(null);
  const [downloadFormat, setDownloadFormat] = useState<ExportFormat | null>(
    null,
  );

  // certificate details modal
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [viewCert, setViewCert] = useState<Certificate | null>(null);

  // modals
  const [rootOpen, setRootOpen] = useState(false);
  const [interOpen, setInterOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);

  const [rootForm] = Form.useForm();
  const [interForm] = Form.useForm();
  const [issueForm] = Form.useForm();
  const [policyForm] = Form.useForm();
  const [templateForm] = Form.useForm();

  const caById = useMemo(
    () => Object.fromEntries(authorities.map((a) => [a.id, a])),
    [authorities],
  );

  const notifyError = (err: any, fallback = "Request failed") => {
    const msg = err?.message || err?.body?.message || fallback;
    open?.({ type: "error", message: msg });
    console.error(err);
  };

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const [caRes, certRes, polRes, tplRes, grpRes] = await Promise.all([
        dp.getList<CertAuthority>({
          resource: "certificate_authorities",
          pagination: { current: 1, pageSize: 500 },
          sorters: [{ field: "created_at", order: "desc" }],
          meta: { schema: CA_SCHEMA },
        }),
        dp.getList<Certificate>({
          resource: "certificates",
          pagination: { current: 1, pageSize: 500 },
          sorters: [{ field: "created_at", order: "desc" }],
          meta: { schema: CA_SCHEMA },
        }),
        dp.getList<IssuancePolicy>({
          resource: "issuance_policies",
          pagination: { current: 1, pageSize: 500 },
          sorters: [{ field: "created_at", order: "desc" }],
          meta: { schema: CA_SCHEMA },
        }),
        dp.getList<Template>({
          resource: "certificate_templates",
          pagination: { current: 1, pageSize: 500 },
          sorters: [{ field: "created_at", order: "desc" }],
          meta: { schema: CA_SCHEMA },
        }),
        dp.getList({
          resource: "groups",
          pagination: { current: 1, pageSize: 2000 },
          meta: { schema: "iam" },
        }),
      ]);
      setAuthorities(caRes.data);
      setCerts(certRes.data);
      setPolicies(polRes.data);
      setTemplates(tplRes.data);
      setGroups(grpRes.data);
    } catch (err: any) {
      notifyError(err, "Load failed");
    } finally {
      setLoading(false);
    }
  }, [dp]);

  // ✅ REALTIME SUBSCRIBE (matches your working ProjectControl pattern)
  const realtimeTopics = useMemo(() => {
    // Subscribe to core objects + share tables.
    // You can reduce this later if you want by gating on activeTab.
    return [
      "ca.certificate_authorities",
      "ca.certificates",
      "ca.issuance_policies",
      "ca.certificate_templates",
      "ca.certificate_authority_shares",
      "ca.certificate_shares",
      "ca.issuance_policy_shares",
      "ca.certificate_template_shares",
    ];
  }, []);

  usePGBroadcastRefresh({
    topics: realtimeTopics,
    debounceMs: 150,
    enabled: true,
    onChange: () => {
      // bump refreshKey; effects below reload
      setRefreshKey((k) => k + 1);
    },
  });

  // initial + realtime reload
  useEffect(() => {
    loadAll();
  }, [loadAll, refreshKey]);

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    message.info("Copied");
  };

  const apiBase = dp.getApiUrl().replace(/\/$/, "");
  const runRpc = async (name: string, body: Record<string, any>) => {
    return dp.custom({
      url: `${apiBase}/rpc/${name}`,
      method: "POST",
      meta: { schema: CA_SCHEMA },
      headers: { "Content-Type": "application/json" },
      data: body,
    });
  };

  // Certificate export helpers
  const exportCert = async (
    cert: Certificate,
    format: ExportFormat,
    outputPassword: string | null,
  ) => {
    try {
      setDownloadSubmitting(true);

      const res = await runRpc("export_certificate", {
        p_cert_id: cert.id,
        p_format: format,
        p_master_password: masterPassword || null,
        p_output_password: outputPassword,
        p_include_chain: format === "pem" ? false : true,
      });

      const blob = byteStringToBlob(
        (res as any)?.data ?? res,
        mimeFromFormat(format),
      );
      const filename = filenameFromCertAndFormat(cert, format);
      downloadFile(blob, filename);
      message.success(`Downloaded ${format.toUpperCase()}`);
    } catch (err: any) {
      notifyError(err, "Download failed");
    } finally {
      setDownloadSubmitting(false);
    }
  };

  const openDownloadForCert = (cert: Certificate, format: ExportFormat) => {
    if (formatNeedsPassword(format)) {
      setDownloadRow(cert);
      setDownloadFormat(format);
      setDownloadPassword("");
      setDownloadOpen(true);
      return;
    }
    void exportCert(cert, format, null);
  };

  const handleDownloadConfirm = async () => {
    if (!downloadRow || !downloadFormat) return;
    if (!downloadPassword) {
      message.warning("Please enter a password for this export");
      return;
    }
    await exportCert(downloadRow, downloadFormat, downloadPassword);
    setDownloadOpen(false);
    setDownloadRow(null);
    setDownloadFormat(null);
    setDownloadPassword("");
  };

  const handleDownloadCancel = () => {
    setDownloadOpen(false);
    setDownloadRow(null);
    setDownloadFormat(null);
    setDownloadPassword("");
  };

  const copyPemForCert = async (
    cert: Certificate,
    opts: { includeChain: boolean; withKey: boolean },
  ) => {
    const { includeChain, withKey } = opts;
    const format: ExportFormat = withKey
      ? includeChain
        ? "chain_pem_key"
        : "pem_key"
      : includeChain
        ? "chain_pem"
        : "pem";

    try {
      const res = await dp.custom({
        url: `${apiBase}/rpc/export_certificate_pem_text`,
        method: "POST",
        meta: { schema: CA_SCHEMA },
        headers: { "Content-Type": "application/json" },
        data: {
          p_cert_id: cert.id,
          p_format: format,
          p_include_chain: includeChain,
          p_master_password: withKey ? masterPassword || null : null,
        },
      });

      const data = (res as any).data;
      let text: string | undefined;

      if (typeof data === "string") {
        text = data;
      } else if (data && typeof data === "object") {
        const firstVal = Object.values(data)[0];
        if (typeof firstVal === "string") text = firstVal;
      }

      if (!text) {
        throw new Error("Unexpected response from export_certificate_pem_text");
      }

      copyToClipboard(text);
    } catch (err: any) {
      notifyError(err, "Copy PEM failed");
    }
  };

  const openDetails = (cert: Certificate) => {
    setViewCert(cert);
    setDetailsOpen(true);
  };

  const revokeCert = async (cert: Certificate) => {
    try {
      await dp.update({
        resource: "certificates",
        id: cert.id,
        variables: { revoked_at: new Date().toISOString() },
        meta: { schema: CA_SCHEMA },
      });
      message.success("Certificate revoked");
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      notifyError(err, "Revoke failed");
    }
  };

  const deleteCert = async (cert: Certificate) => {
    try {
      await dp.delete({
        resource: "certificates",
        id: cert.id,
        meta: { schema: CA_SCHEMA },
      });
      message.success("Certificate deleted");
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      notifyError(err, "Delete failed");
    }
  };

  // Submit handlers
  const submitRoot = async () => {
    try {
      const values = await rootForm.validateFields();
      await runRpc("create_certificate_authority_openssl", {
        p_ca_name: values.ca_name,
        p_common_name: values.common_name,
        p_organization: values.organization || null,
        p_organizational_unit: values.organizational_unit || null,
        p_country: values.country || null,
        p_state_province: values.state_province || null,
        p_locality: values.locality || null,
        p_email: values.email || null,
        p_key_bits: values.key_bits,
        p_hash_alg_code: values.hash_alg,
        p_validity_days: values.validity_days,
        p_master_password: masterPassword || null,
        p_created_by: "ui",
      });
      message.success("Root CA created");
      setRootOpen(false);
      rootForm.resetFields();
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      if (err?.errorFields) return;
      notifyError(err, "Create root CA failed");
    }
  };

  const submitIntermediate = async () => {
    try {
      const values = await interForm.validateFields();
      await runRpc("create_intermediate_certificate_authority_openssl", {
        p_parent_ca_id: values.parent_ca_id,
        p_ca_name: values.ca_name,
        p_common_name: values.common_name,
        p_organization: values.organization || null,
        p_organizational_unit: values.organizational_unit || null,
        p_country: values.country || null,
        p_state_province: values.state_province || null,
        p_locality: values.locality || null,
        p_email: values.email || null,
        p_path_length_constraint: values.path_len ?? 0,
        p_key_bits: values.key_bits,
        p_hash_alg_code: values.hash_alg,
        p_validity_days: values.validity_days,
        p_master_password: masterPassword || null,
        p_created_by: "ui",
      });
      message.success("Intermediate CA created");
      setInterOpen(false);
      interForm.resetFields();
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      if (err?.errorFields) return;
      notifyError(err, "Create intermediate CA failed");
    }
  };

  const submitIssue = async () => {
    try {
      const values = await issueForm.validateFields();
      await runRpc("issue_leaf_certificate_openssl", {
        p_common_name: values.common_name,
        p_created_by: "ui",
        p_hash_alg_code: values.hash_alg,
        p_issuing_ca_id: values.issuing_ca_id,
        p_key_bits: values.key_bits,
        p_master_password: masterPassword || null,
        p_san_list: values.sans || null,
        p_validity_days: values.validity_days,
        p_organization: values.organization || null,
        p_organizational_unit: values.organizational_unit || null,
        p_country: values.country || null,
        p_state_province: values.state_province || null,
        p_locality: values.locality || null,
        p_email: values.email || null,
      });
      message.success("Certificate issued");
      setIssueOpen(false);
      issueForm.resetFields();
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      if (err?.errorFields) return;
      notifyError(err, "Issue certificate failed");
    }
  };

  const submitPolicy = async () => {
    try {
      const values = await policyForm.validateFields();
      const allowedSanSuffixes =
        typeof values.allowed_san_dns_suffix_input === "string" &&
        values.allowed_san_dns_suffix_input.trim().length > 0
          ? values.allowed_san_dns_suffix_input
              .split(",")
              .map((s: string) => s.trim())
              .filter(Boolean)
          : null;

      const payload: any = {
        ca_id: values.ca_id,
        policy_name: values.policy_name,
        description: values.description || null,
        priority: values.priority !== undefined ? values.priority : 100,
        is_active: values.is_active,
        min_validity_days: values.min_validity_days ?? null,
        max_validity_days: values.max_validity_days ?? null,
        require_san: values.require_san ?? false,
        cn_regex: values.cn_regex || null,
        allowed_san_dns_suffix: allowedSanSuffixes,
      };

      if (values.id) {
        await dp.update({
          resource: "issuance_policies",
          id: values.id,
          variables: payload,
          meta: { schema: CA_SCHEMA },
        });
      } else {
        await dp.create({
          resource: "issuance_policies",
          variables: payload,
          meta: { schema: CA_SCHEMA },
        });
      }
      message.success("Policy saved");
      setPolicyOpen(false);
      policyForm.resetFields();
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      if (err?.errorFields) return;
      notifyError(err, "Save policy failed");
    }
  };

  const submitTemplate = async () => {
    try {
      const values = await templateForm.validateFields();
      const {
        id,
        template_name,
        cert_type_id,
        key_algorithm_id,
        key_size,
        hash_algorithm_id,
        validity_period_days,
        key_usage_input,
        extended_key_usage_input,
        subject_template,
        san_template,
        is_active,
      } = values;

      let keyUsageArr: string[] = ["digitalSignature", "keyEncipherment"];
      if (
        typeof key_usage_input === "string" &&
        key_usage_input.trim().length > 0
      ) {
        keyUsageArr = key_usage_input
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean);
        if (keyUsageArr.length === 0)
          keyUsageArr = ["digitalSignature", "keyEncipherment"];
      }

      let ekuArr: string[] | null = null;
      if (
        typeof extended_key_usage_input === "string" &&
        extended_key_usage_input.trim().length > 0
      ) {
        const tmp = extended_key_usage_input
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean);
        ekuArr = tmp.length ? tmp : null;
      }

      let subjectTemplateJson: any = undefined;
      if (subject_template && typeof subject_template === "string") {
        const trimmed = subject_template.trim();
        if (trimmed) {
          try {
            subjectTemplateJson = JSON.parse(trimmed);
          } catch (e) {
            message.error("Subject template must be valid JSON");
            return;
          }
        }
      }

      let sanTemplateJson: any = undefined;
      if (san_template && typeof san_template === "string") {
        const trimmed = san_template.trim();
        if (trimmed) {
          try {
            sanTemplateJson = JSON.parse(trimmed);
          } catch (e) {
            message.error("SAN template must be valid JSON");
            return;
          }
        }
      }

      const payload: any = {
        template_name,
        cert_type_id,
        key_algorithm_id,
        key_size,
        hash_algorithm_id,
        validity_period_days,
        key_usage: keyUsageArr,
        extended_key_usage: ekuArr,
        is_active,
      };
      if (subjectTemplateJson !== undefined)
        payload.subject_template = subjectTemplateJson;
      if (sanTemplateJson !== undefined) payload.san_template = sanTemplateJson;

      if (id) {
        await dp.update({
          resource: "certificate_templates",
          id,
          variables: payload,
          meta: { schema: CA_SCHEMA },
        });
      } else {
        await dp.create({
          resource: "certificate_templates",
          variables: payload,
          meta: { schema: CA_SCHEMA },
        });
      }

      message.success("Template saved");
      setTemplateOpen(false);
      templateForm.resetFields();
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      if (err?.errorFields) return;
      notifyError(err, "Save template failed");
    }
  };

  /* ------------ Columns ------------ */
  // Authorities
  const baseAuthCols: ColumnsType<CertAuthority> = [
    {
      title: "Type",
      dataIndex: "is_root",
      width: 110,
      sorter: (a, b) => Number(a.is_root) - Number(b.is_root),
      render: (v: boolean) => (v ? "Root" : "Intermediate"),
    },
    {
      title: "CA Name",
      dataIndex: "ca_name",
      sorter: (a, b) => a.ca_name.localeCompare(b.ca_name),
    },
    {
      title: "Common Name",
      dataIndex: "common_name",
      sorter: (a, b) => a.common_name.localeCompare(b.common_name),
    },
    {
      title: "Parent",
      dataIndex: "parent_ca_id",
      render: (v: UUID | null) => (v ? caById[v]?.common_name || v : "-"),
      sorter: (a, b) => {
        const av = a.parent_ca_id
          ? caById[a.parent_ca_id!]?.common_name || ""
          : "";
        const bv = b.parent_ca_id
          ? caById[b.parent_ca_id!]?.common_name || ""
          : "";
        return av.localeCompare(bv);
      },
    },
    {
      title: "Valid",
      width: 210,
      render: (r) =>
        `${r.not_before.slice(0, 10)} → ${r.not_after.slice(0, 10)}`,
      sorter: (a, b) => a.not_after.localeCompare(b.not_after),
    },
    {
      title: "Status",
      dataIndex: "status_id",
      width: 100,
    },
    {
      title: "Actions",
      width: 100,
      render: (r) => (
        <Button
          icon={<CopyOutlined />}
          size="small"
          onClick={() => copyToClipboard(r.certificate_pem)}
        />
      ),
    },
  ];
  const { columns: authColumns, components: authComponents } =
    useResizableColumns<CertAuthority>(
      baseAuthCols,
      [110, 180, 220, 220, 210, 100, 100],
    );

  // Certificates
  const baseCertCols: ColumnsType<Certificate> = [
    {
      title: "",
      key: "actions",
      width: 60,
      fixed: "left",
      render: (_value, record) => {
        const items: MenuProps["items"] = [
          {
            key: "view-details",
            icon: <FileTextOutlined />,
            label: "View Details",
          },
          { type: "divider" },
          {
            key: "download-pem",
            icon: <DownloadOutlined />,
            label: "Download PEM",
          },
          {
            key: "download-chain-pem",
            icon: <DownloadOutlined />,
            label: "Download Chain",
          },
          {
            key: "download-p12",
            icon: <DownloadOutlined />,
            label: "Download P12/PFX",
          },
          { type: "divider" },
          { key: "revoke", label: "Revoke" },
          { key: "delete", label: "Delete" },
        ];
        return (
          <Dropdown
            trigger={["click"]}
            menu={{
              items,
              onClick: ({ key }) => {
                switch (key) {
                  case "view-details":
                    openDetails(record);
                    break;
                  case "download-pem":
                    openDownloadForCert(record, "pem");
                    break;
                  case "download-chain-pem":
                    openDownloadForCert(record, "chain_pem");
                    break;
                  case "download-p12":
                    openDownloadForCert(record, "p12");
                    break;
                  case "revoke":
                    Modal.confirm({
                      title: "Revoke certificate?",
                      content: "Sets revoked_at to now.",
                      okText: "Revoke",
                      okButtonProps: { danger: true },
                      onOk: () => revokeCert(record),
                    });
                    break;
                  case "delete":
                    Modal.confirm({
                      title: "Delete certificate?",
                      content: "Permanently delete this row.",
                      okText: "Delete",
                      okButtonProps: { danger: true },
                      onOk: () => deleteCert(record),
                    });
                    break;
                }
              },
            }}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
    {
      title: "Common Name",
      dataIndex: "common_name",
      sorter: (a, b) => a.common_name.localeCompare(b.common_name),
    },
    {
      title: "Issuing CA",
      dataIndex: "issuing_ca_id",
      render: (v) => caById[v]?.common_name || v,
      sorter: (a, b) =>
        (caById[a.issuing_ca_id]?.common_name || "").localeCompare(
          caById[b.issuing_ca_id]?.common_name || "",
        ),
    },
    {
      title: "SANs",
      dataIndex: "subject_alternative_names",
      width: 260,
      render: (v?: string[] | null) => (
        <Input.TextArea
          value={v?.join(", ") || ""}
          readOnly
          autoSize={{ minRows: 1, maxRows: 3 }}
        />
      ),
    },
    {
      title: "Valid",
      width: 210,
      render: (r) =>
        `${r.not_before.slice(0, 10)} → ${r.not_after.slice(0, 10)}`,
      sorter: (a, b) => a.not_after.localeCompare(b.not_after),
    },
    {
      title: "Revoked",
      width: 120,
      render: (r) =>
        r.revoked_at ? (
          <Tag color="red">{r.revoked_at.slice(0, 10)}</Tag>
        ) : (
          <Tag color="green">Active</Tag>
        ),
      sorter: (a, b) => (a.revoked_at || "").localeCompare(b.revoked_at || ""),
    },
    { title: "Status", dataIndex: "status_id", width: 80 },
  ];
  const { columns: certColumns, components: certComponents } =
    useResizableColumns<Certificate>(
      baseCertCols,
      [70, 220, 220, 260, 210, 120, 80],
    );

  // Policies
  const basePolCols: ColumnsType<IssuancePolicy> = [
    {
      title: "",
      key: "actions",
      width: 60,
      fixed: "left",
      render: (_value, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit" },
              { key: "delete", label: "Delete" },
            ],
            onClick: ({ key }) => {
              if (key === "edit") {
                policyForm.setFieldsValue({
                  id: record.id,
                  ca_id: record.ca_id,
                  policy_name: record.policy_name,
                  description: record.description ?? "",
                  priority: record.priority ?? 100,
                  is_active: record.is_active,
                  require_san: record.require_san ?? false,
                  min_validity_days: record.min_validity_days ?? undefined,
                  max_validity_days: record.max_validity_days ?? undefined,
                  cn_regex: record.cn_regex ?? "",
                  allowed_san_dns_suffix_input:
                    record.allowed_san_dns_suffix?.join(",") ?? "",
                });
                setPolicyOpen(true);
              } else if (key === "delete") {
                Modal.confirm({
                  title: "Delete policy?",
                  okText: "Delete",
                  okButtonProps: { danger: true },
                  onOk: async () => {
                    await dp.delete({
                      resource: "issuance_policies",
                      id: record.id,
                      meta: { schema: CA_SCHEMA },
                    });
                    setRefreshKey((k) => k + 1);
                  },
                });
              }
            },
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
    {
      title: "CA",
      dataIndex: "ca_id",
      render: (v) => caById[v]?.common_name || v,
    },
    {
      title: "Name",
      dataIndex: "policy_name",
      sorter: (a, b) => a.policy_name.localeCompare(b.policy_name),
    },
    {
      title: "Active",
      dataIndex: "is_active",
      width: 90,
      render: (v: boolean) => (v ? "Yes" : "No"),
    },
    { title: "Priority", dataIndex: "priority", width: 110 },
  ];
  const { columns: polColumns, components: polComponents } =
    useResizableColumns<IssuancePolicy>(basePolCols, [60, 220, 220, 90, 110]);

  // Templates
  const baseTplCols: ColumnsType<Template> = [
    {
      title: "",
      key: "actions",
      width: 60,
      fixed: "left",
      render: (_value, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit" },
              { key: "delete", label: "Delete" },
            ],
            onClick: ({ key }) => {
              if (key === "edit") {
                templateForm.setFieldsValue({
                  id: record.id,
                  template_name: record.template_name,
                  cert_type_id: record.cert_type_id,
                  key_algorithm_id: record.key_algorithm_id,
                  key_size: record.key_size,
                  hash_algorithm_id: record.hash_algorithm_id,
                  validity_period_days: record.validity_period_days,
                  key_usage_input: record.key_usage.join(","),
                  extended_key_usage_input:
                    record.extended_key_usage?.join(",") ?? "",
                  subject_template:
                    record.subject_template != null
                      ? JSON.stringify(record.subject_template, null, 2)
                      : "",
                  san_template:
                    record.san_template != null
                      ? JSON.stringify(record.san_template, null, 2)
                      : "",
                  is_active: record.is_active,
                });
                setTemplateOpen(true);
              } else if (key === "delete") {
                Modal.confirm({
                  title: "Delete template?",
                  okText: "Delete",
                  okButtonProps: { danger: true },
                  onOk: async () => {
                    await dp.delete({
                      resource: "certificate_templates",
                      id: record.id,
                      meta: { schema: CA_SCHEMA },
                    });
                    setRefreshKey((k) => k + 1);
                  },
                });
              }
            },
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
    {
      title: "Name",
      dataIndex: "template_name",
      sorter: (a, b) => a.template_name.localeCompare(b.template_name),
    },
    { title: "Key Size", dataIndex: "key_size", width: 120 },
    { title: "Validity", dataIndex: "validity_period_days", width: 140 },
    {
      title: "Active",
      dataIndex: "is_active",
      width: 100,
      render: (v: boolean) => (v ? "Yes" : "No"),
    },
  ];
  const { columns: tplColumns, components: tplComponents } =
    useResizableColumns<Template>(baseTplCols, [60, 220, 120, 140, 100]);

  const searchLower = search.toLowerCase();

  return (
    <div>
      <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <Input.Password
          placeholder="Master password (optional)"
          value={masterPassword}
          onChange={(e) => setMasterPassword(e.target.value)}
          style={{ width: 260 }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => setRefreshKey((k) => k + 1)}
          loading={loading}
        >
          Refresh
        </Button>
        <Input.Search
          placeholder="Search Common Name or SANs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260 }}
        />
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            rootForm.resetFields();
            setRootOpen(true);
          }}
        >
          New Root CA
        </Button>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            interForm.resetFields();
            setInterOpen(true);
          }}
        >
          New Intermediate CA
        </Button>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            issueForm.resetFields();
            setIssueOpen(true);
          }}
        >
          Issue Certificate
        </Button>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            policyForm.resetFields();
            setPolicyOpen(true);
          }}
        >
          New Policy
        </Button>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            templateForm.resetFields();
            setTemplateOpen(true);
          }}
        >
          New Template
        </Button>
      </Space>

      <Card>
        <Tabs activeKey={activeTab} onChange={(k) => setActiveTab(k as any)}>
          <Tabs.TabPane
            tab={
              <span>
                <KeyOutlined /> Authorities
              </span>
            }
            key="authorities"
          >
            <Table<CertAuthority>
              dataSource={authorities.filter(
                (a) =>
                  a.common_name.toLowerCase().includes(searchLower) ||
                  a.ca_name.toLowerCase().includes(searchLower),
              )}
              rowKey="id"
              size="small"
              loading={loading}
              pagination={false}
              components={authComponents}
              columns={authColumns}
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <FileTextOutlined /> Certificates
              </span>
            }
            key="certificates"
          >
            <Table<Certificate>
              dataSource={certs.filter(
                (c) =>
                  c.common_name.toLowerCase().includes(searchLower) ||
                  (c.subject_alternative_names || []).some((s) =>
                    s.toLowerCase().includes(searchLower),
                  ),
              )}
              rowKey="id"
              size="small"
              loading={loading || downloadSubmitting}
              pagination={false}
              components={certComponents}
              columns={certColumns}
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <SettingOutlined /> Policies
              </span>
            }
            key="policies"
          >
            <Table<IssuancePolicy>
              dataSource={policies.filter((p) =>
                p.policy_name.toLowerCase().includes(searchLower),
              )}
              rowKey="id"
              size="small"
              loading={loading}
              pagination={false}
              components={polComponents}
              columns={polColumns}
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <FormOutlined /> Templates
              </span>
            }
            key="templates"
          >
            <Table<Template>
              dataSource={templates.filter((t) =>
                t.template_name.toLowerCase().includes(searchLower),
              )}
              rowKey="id"
              size="small"
              loading={loading || downloadSubmitting}
              pagination={false}
              components={tplComponents}
              columns={tplColumns}
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <ShareAltOutlined /> CA Shares
              </span>
            }
            key="ca_shares"
          >
            <SharesBrowser
              title="CA Shares"
              shareResource="certificate_authority_shares"
              fkField="certificate_authority_id"
              parentResource="certificate_authorities"
              parentLabel={(r) => `${r.common_name} (${r.ca_name})`}
              refreshToken={refreshKey}
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <ShareAltOutlined /> Cert Shares
              </span>
            }
            key="cert_shares"
          >
            <SharesBrowser
              title="Certificate Shares"
              shareResource="certificate_shares"
              fkField="certificate_id"
              parentResource="certificates"
              parentLabel={(r) =>
                `${r.common_name} (issued by ${
                  caById[r.issuing_ca_id]?.common_name || r.issuing_ca_id
                })`
              }
              refreshToken={refreshKey}
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <ShareAltOutlined /> Policy Shares
              </span>
            }
            key="policy_shares"
          >
            <SharesBrowser
              title="Policy Shares"
              shareResource="issuance_policy_shares"
              fkField="issuance_policy_id"
              parentResource="issuance_policies"
              parentLabel={(r) => r.policy_name}
              refreshToken={refreshKey}
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <ShareAltOutlined /> Template Shares
              </span>
            }
            key="template_shares"
          >
            <SharesBrowser
              title="Template Shares"
              shareResource="certificate_template_shares"
              fkField="certificate_template_id"
              parentResource="certificate_templates"
              parentLabel={(r) => r.template_name}
              refreshToken={refreshKey}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* Root CA Modal */}
      <Modal
        open={rootOpen}
        onCancel={() => setRootOpen(false)}
        onOk={submitRoot}
        title="New Root CA"
        okText="Create"
      >
        <Form layout="vertical" form={rootForm}>
          <Form.Item name="ca_name" label="CA Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="common_name"
            label="Common Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="validity_days"
            label="Validity (days)"
            initialValue={3650}
          >
            <InputNumber min={365} max={3650} />
          </Form.Item>
          <Form.Item name="key_bits" label="Key bits" initialValue={4096}>
            <InputNumber min={2048} max={8192} step={1024} />
          </Form.Item>
          <Form.Item
            name="hash_alg"
            label="Hash Algorithm"
            initialValue="sha256"
          >
            <Select options={[{ value: "sha256", label: "SHA256" }]} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={interOpen}
        onCancel={() => setInterOpen(false)}
        onOk={submitIntermediate}
        title="New Intermediate CA"
        okText="Create"
      >
        <Form layout="vertical" form={interForm}>
          <Form.Item
            name="parent_ca_id"
            label="Parent CA"
            rules={[{ required: true }]}
          >
            <Select
              options={authorities
                .filter((a) => a.is_root)
                .map((a) => ({ value: a.id, label: a.common_name }))}
            />
          </Form.Item>
          <Form.Item
            name="common_name"
            label="Common Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={issueOpen}
        onCancel={() => setIssueOpen(false)}
        onOk={submitIssue}
        title="Issue Leaf Certificate"
        okText="Issue"
      >
        <Form layout="vertical" form={issueForm}>
          <Form.Item
            name="issuing_ca_id"
            label="Issuing CA"
            rules={[{ required: true }]}
          >
            <Select
              options={authorities
                .filter((a) => !a.is_root)
                .map((a) => ({ value: a.id, label: a.common_name }))}
            />
          </Form.Item>
          <Form.Item
            name="common_name"
            label="Subject CN"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="sans" label="SANs (comma)">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={policyOpen}
        onCancel={() => setPolicyOpen(false)}
        onOk={submitPolicy}
        title="Issuance Policy"
        okText="Save"
      >
        <Form layout="vertical" form={policyForm}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="ca_id" label="CA" rules={[{ required: true }]}>
            <Select
              options={authorities.map((a) => ({
                value: a.id,
                label: a.common_name,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="policy_name"
            label="Policy Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={templateOpen}
        onCancel={() => setTemplateOpen(false)}
        onOk={submitTemplate}
        title="Certificate Template"
        okText="Save"
      >
        <Form layout="vertical" form={templateForm}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="template_name"
            label="Template Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={downloadOpen}
        title="Password"
        onCancel={handleDownloadCancel}
        onOk={handleDownloadConfirm}
        confirmLoading={downloadSubmitting}
      >
        <Input.Password
          value={downloadPassword}
          onChange={(e) => setDownloadPassword(e.target.value)}
          placeholder="Output password"
        />
      </Modal>

      <Modal
        open={detailsOpen}
        onCancel={() => setDetailsOpen(false)}
        footer={null}
        title={
          viewCert ? `Certificate Details — ${viewCert.common_name}` : "Details"
        }
        width={800}
      >
        {viewCert && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <strong>Common Name:</strong> {viewCert.common_name}
            </div>
            <div>
              <strong>Issuing CA:</strong>{" "}
              {caById[viewCert.issuing_ca_id]?.common_name ??
                viewCert.issuing_ca_id}
            </div>
            <div>
              <strong>Certificate PEM:</strong>{" "}
              <Input.TextArea
                value={viewCert.certificate_pem}
                readOnly
                autoSize={{ minRows: 8, maxRows: 16 }}
              />
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default CAControl;

